import { getGlobalScope } from './global-scope';
import { pruneJson } from './utils/json-query';
import { SAFE_HEADERS, FORBIDDEN_HEADERS } from './';

/* SAFE TYPE DEFINITIONS
  These type definitions expose limited properties of the original types
  to prevent the consumer from mutating them or consuming them.
*/
type BlobSafe = {
  size: number;
};

type ArrayBufferSafe = {
  byteLength: number;
};

type ArrayBufferViewSafe = {
  byteLength: number;
};

type URLSearchParamsSafe = {
  toString(): string;
};

// no method on readablestream is safe to call
type ReadableStreamSafe = Record<string, never>;

type FormDataEntryValueSafe = string | BlobSafe | null;

type BodyInitSafe =
  | string
  | Blob
  | ArrayBufferSafe
  | FormDataSafe
  | URLSearchParamsSafe
  | ArrayBufferViewSafe
  | null
  | undefined;

type HeadersRequestSafe = {
  entries(): IterableIterator<[string, string]>;
  forEach(callbackfn: (value: string, key: string) => void): void;
};

type HeadersResponseSafe = {
  get(name: string): string | null;
  forEach(callbackfn: (value: string, key: string) => void): void;
};

type HeadersInitSafe = HeadersRequestSafe | Record<string, string> | string[][];

type ResponseSafe = {
  status: number;
  headers: HeadersResponseSafe | undefined;
  clone(): ResponseCloneSafe;
};

type ResponseCloneSafe = {
  text(): Promise<string>;
};

const TEXT_READ_TIMEOUT = 500;

export type RequestInitSafe = {
  method?: string;
  headers?: HeadersInitSafe;
  body?: BodyInitSafe;
};
export interface FormDataSafe {
  entries(): IterableIterator<[string, FormDataEntryValueSafe]>;
}
export type XMLHttpRequestBodyInitSafe = BlobSafe | FormDataSafe | URLSearchParamsSafe | string;

export type FetchRequestBody =
  | string
  | BlobSafe
  | ArrayBufferSafe
  | FormDataSafe
  | URLSearchParamsSafe
  | ArrayBufferViewSafe
  | null
  | undefined;

export interface IRequestWrapper {
  /**
   * Get the headers of the request.
   * @param allow - The headers to allow.
   * @returns The pruned headers
   */
  headers(allow?: string[]): Record<string, string> | undefined;
  bodySize?: number;
  method?: string;
  body?: FetchRequestBody | XMLHttpRequestBodyInitSafe | null;
  json: (allow?: string[], exclude?: string[]) => Promise<JsonObject | null>;
}

export const MAXIMUM_ENTRIES = 100;

/**
 * This class encapsulates the RequestInit (https://developer.mozilla.org/en-US/docs/Web/API/RequestInit)
 * object so that the consumer can only get access to the headers, method and body size.
 *
 * This is to prevent consumers from directly accessing the Request object
 * and mutating it or running costly operations on it.
 *
 * IMPORTANT:
 *    * Do not make changes to this class without careful consideration
 *      of performance implications, memory usage and potential to mutate the customer's
 *      request.
 *   * NEVER .clone() the RequestInit object. This will 2x's the memory overhead of the request
 *   * NEVER: call .arrayBuffer(), text(), json() or any other method on the body that
 *     consumes the body's stream. This will cause the response to be consumed
 *     meaning the body will be empty when the customer tries to access it.
 *     (ie: if the body is an instanceof https://developer.mozilla.org/en-US/docs/Web/API/ReadableStream
 *      never call any of the methods on it)
 */
export class RequestWrapperFetch implements IRequestWrapper {
  private _bodySize: number | undefined;
  constructor(private request: RequestInitSafe) {}

  headers(allow: string[] = []): Record<string, string> | undefined {
    const headersUnsafe = this.request.headers;

    // copy the headers into a new object
    const headersSafeCopy: Record<string, string> = {};
    if (Array.isArray(headersUnsafe)) {
      headersUnsafe.forEach(([headerName, headerValue]) => {
        headersSafeCopy[headerName] = headerValue;
      });
    } else if (headersUnsafe instanceof Headers) {
      headersUnsafe.forEach((value: string, key: string) => {
        headersSafeCopy[key] = value;
      });
    } else if (typeof headersUnsafe === 'object' && headersUnsafe !== null) {
      for (const [key, value] of Object.entries(headersUnsafe as Record<string, string>)) {
        headersSafeCopy[key] = value;
      }
    }

    return pruneHeaders(headersSafeCopy, { allow });
  }

  get bodySize(): number | undefined {
    if (typeof this._bodySize === 'number') return this._bodySize;
    const global = getGlobalScope();

    /* istanbul ignore if */
    if (!global?.TextEncoder) {
      return;
    }
    const body = this.request.body as FetchRequestBody;
    this._bodySize = getBodySize(body, MAXIMUM_ENTRIES);
    return this._bodySize;
  }

  get method(): string | undefined {
    return this.request.method;
  }

  get body(): string | null {
    if (typeof this.request.body === 'string') {
      return this.request.body;
    }
    return null;
  }

  async json(allow: string[] = [], exclude: string[] = []): Promise<JsonObject | null> {
    if (allow.length === 0) {
      return null;
    }
    const text = this.body;
    return safeParseAndPruneBody(text, allow, exclude);
  }
}

export class RequestWrapperXhr implements IRequestWrapper {
  constructor(readonly bodyRaw: XMLHttpRequestBodyInitSafe | null, readonly requestHeaders: Record<string, string>) {}

  headers(allow: string[] = []): Record<string, string> | undefined {
    return pruneHeaders(this.requestHeaders, { allow });
  }

  get bodySize(): number | undefined {
    return getBodySize(this.bodyRaw as FetchRequestBody, MAXIMUM_ENTRIES);
  }

  get body(): string | null {
    if (typeof this.bodyRaw === 'string') {
      return this.bodyRaw;
    }
    return null;
  }

  async json(allow: string[] = [], exclude: string[] = []): Promise<JsonObject | null> {
    if (allow.length === 0) {
      return null;
    }
    const text = this.body;
    return safeParseAndPruneBody(text, allow, exclude);
  }
}

function getBodySize(bodyUnsafe: FetchRequestBody, maxEntries: number): number | undefined {
  let bodySize: number | undefined;
  const global = getGlobalScope();
  /* istanbul ignore next */
  const TextEncoder = global?.TextEncoder;
  /* istanbul ignore next */
  if (!TextEncoder) {
    return;
  }
  let bodySafe;
  if (typeof bodyUnsafe === 'string') {
    bodySafe = bodyUnsafe;
    bodySize = new TextEncoder().encode(bodySafe).length;
  } else if (bodyUnsafe instanceof Blob) {
    bodySafe = bodyUnsafe as BlobSafe;
    bodySize = bodySafe.size;
  } else if (bodyUnsafe instanceof URLSearchParams) {
    bodySafe = bodyUnsafe as URLSearchParamsSafe;
    bodySize = new TextEncoder().encode(bodySafe.toString()).length;
  } else if (ArrayBuffer.isView(bodyUnsafe)) {
    bodySafe = bodyUnsafe as ArrayBufferViewSafe;
    bodySize = bodySafe.byteLength;
  } else if (bodyUnsafe instanceof ArrayBuffer) {
    bodySafe = bodyUnsafe as ArrayBufferSafe;
    bodySize = bodySafe.byteLength;
  } else if (bodyUnsafe instanceof FormData) {
    // Estimating only for text parts; not accurate for files
    const formData = bodyUnsafe as unknown as FormDataSafe;

    let total = 0;
    let count = 0;
    for (const [key, value] of formData.entries()) {
      total += key.length;
      if (typeof value === 'string') {
        total += new TextEncoder().encode(value).length;
      } else if (value instanceof Blob) {
        total += value.size;
      } else {
        // encountered an unknown type
        // we can't estimate the size of this entry
        return;
      }
      // terminate if we reach the maximum number of entries
      // to avoid performance issues in case of very large FormData
      if (++count >= maxEntries) {
        return;
      }
    }
    bodySize = total;
  } else if (bodyUnsafe instanceof ReadableStream) {
    // If bodyUnsafe is an instanceof ReadableStream, we can't determine the size,
    // without consuming it, so we return undefined.
    // Never ever consume ReadableStream! DO NOT DO IT!!!
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    bodySafe = bodyUnsafe as unknown as ReadableStreamSafe;
    return;
  }
  return bodySize;
}

export type JsonObject = {
  [key: string]: JsonValue;
};

export type JsonValue = string | number | boolean | null | JsonObject | JsonArray;

export type JsonArray = Array<JsonValue>;

export interface IResponseWrapper {
  /**
   * Get the headers of the response.
   * @param allow - The headers to allow.
   * @returns The pruned headers
   */
  headers(allow?: string[]): Record<string, string> | undefined;
  bodySize?: number;
  status?: number;
  body?: string | Blob | ReadableStream | ArrayBuffer | FormDataSafe | URLSearchParams | ArrayBufferView | null;
  json: (allow?: string[], exclude?: string[]) => Promise<JsonObject | null>;
}

/**
 * This class encapsulates the Fetch API Response object
 * (https://developer.mozilla.org/en-US/docs/Web/API/Response) so that the consumer can
 * only get access to the headers and body size.
 *
 * This is to prevent consumers from directly accessing the Response object
 * and mutating it or running costly operations on it.
 *
 * IMPORTANT:
 *   * Do not make changes to this class without careful consideration
 *     of performance implications, memory usage and potential to mutate the customer's
 *     response.
 *   * Do not .clone() the Response object unless you need to access the body.
 *     Cloning will 2x the memory overhead of the response.
 *   * NEVER consume the body's stream. This will cause the response to be consumed
 *     meaning the body will be empty when the customer tries to access it.
 *     (ie: if the body is an instanceof https://developer.mozilla.org/en-US/docs/Web/API/ReadableStream
 *      never call any of the methods on it)
 */
export class ResponseWrapperFetch implements IResponseWrapper {
  private _bodySize: number | undefined;
  private clonedResponse?: ResponseCloneSafe;
  constructor(private response: ResponseSafe) {}

  headers(allow: string[] = []): Record<string, string> | undefined {
    if (this.response.headers instanceof Headers) {
      const headersSafe = this.response.headers as HeadersResponseSafe;
      const headersOut: Record<string, string> = {};
      /* istanbul ignore next */
      headersSafe?.forEach?.((value, key) => {
        headersOut[key] = value;
      });
      return pruneHeaders(headersOut, { allow });
    }

    return;
  }

  get bodySize(): number | undefined {
    if (this._bodySize !== undefined) return this._bodySize;
    /* istanbul ignore next */
    const contentLength = this.response.headers?.get?.('content-length');
    const bodySize = contentLength ? parseInt(contentLength, 10) : undefined;
    this._bodySize = bodySize;
    return bodySize;
  }

  get status(): number {
    return this.response.status;
  }

  async text(): Promise<string | null> {
    // !!!IMPORTANT: we clone the response to avoid mutating the original response
    // never call .text(), .json(), etc.. on the original response always clone it first
    if (!this.clonedResponse) {
      this.clonedResponse = this.response.clone();
    }
    try {
      const textPromise = this.clonedResponse.text();
      const timer = new Promise<null>((resolve) =>
        setTimeout(
          /* istanbul ignore next */
          () => resolve(null),
          TEXT_READ_TIMEOUT,
        ),
      );
      const text = await Promise.race<string | null>([textPromise, timer]);
      return text;
    } catch (error) {
      return null;
    }
  }

  async json(allow: string[] = [], exclude: string[] = []): Promise<JsonObject | null> {
    if (allow.length === 0) {
      return null;
    }
    const text = await this.text();
    return safeParseAndPruneBody(text, allow, exclude);
  }
}

export class ResponseWrapperXhr implements IResponseWrapper {
  constructor(
    readonly statusCode: number,
    readonly headersString: string,
    readonly size: number | undefined,
    readonly getJson: () => any | null,
  ) {}

  get bodySize(): number | undefined {
    return this.size;
  }

  get status(): number {
    return this.statusCode;
  }

  headers(allow: string[] = []): Record<string, string> | undefined {
    if (!this.headersString) {
      return {};
    }
    const headers: Record<string, string> = {};
    const headerLines = this.headersString.split('\r\n');
    for (const line of headerLines) {
      const [key, value] = line.split(': ');
      if (key && value) {
        headers[key] = value;
      }
    }
    return pruneHeaders(headers, { allow });
  }

  async json(allow: string[] = [], exclude: string[] = []): Promise<JsonObject | null> {
    if (allow.length === 0) {
      return null;
    }
    const jsonBody = this.getJson() as JsonObject | null;
    if (jsonBody) {
      pruneJson(jsonBody, allow, exclude);
      return jsonBody;
    }
    return null;
  }
}

function safeParseAndPruneBody(text: string | null, allow: string[], exclude: string[]): JsonObject | null {
  if (!text) return null;
  try {
    const json = JSON.parse(text) as JsonObject;
    pruneJson(json, allow, exclude);
    return json;
  } catch (error) {
    return null;
  }
}

export enum PRUNE_STRATEGY {
  REDACT = 'redact',
  REMOVE = 'remove',
}

const REDACTED_VALUE = '[REDACTED]';

/**
 * Prune headers from a headers record object.
 * @param headers - The headers to prune.
 * @param options - The options to prune the headers.
 * @param options.exclude - List of headers to delete from headers
 * @param options.include - List of headers to keep in headers, if not provided, all headers are kept by default
 * @returns The pruned headers.
 */
export const pruneHeaders = (
  headers: Record<string, string>,
  options: {
    allow?: string[];
    strategy?: PRUNE_STRATEGY;
  },
): Record<string, string> => {
  const { allow = [], strategy = PRUNE_STRATEGY.REMOVE } = options;
  const exclude = [...FORBIDDEN_HEADERS];
  const headersPruned: Record<string, string> = {};

  for (const key of Object.keys(headers)) {
    const lowerKey = key.toLowerCase();

    if (exclude.find((e) => e.toLowerCase() === lowerKey)) {
      if (strategy === PRUNE_STRATEGY.REDACT) {
        headersPruned[key] = REDACTED_VALUE;
      }
    } else if (!allow.find((i) => i.toLowerCase() === lowerKey)) {
      if (strategy === PRUNE_STRATEGY.REDACT) {
        headersPruned[key] = REDACTED_VALUE;
      }
    } else {
      headersPruned[key] = headers[key];
    }
  }
  return headersPruned;
};
export class NetworkRequestEvent {
  public requestHeaders?: Record<string, string>;
  public responseHeaders?: Record<string, string>;
  public requestBodyJson?: Promise<JsonObject | null>;
  public responseBodyJson?: Promise<JsonObject | null>;
  constructor(
    public readonly type: 'xhr' | 'fetch',
    public readonly method: string,
    public readonly timestamp: number,
    public readonly startTime: number,
    public readonly url?: string,
    public readonly requestWrapper?: IRequestWrapper,
    public readonly status: number = 0,
    public readonly duration?: number,
    public readonly responseWrapper?: IResponseWrapper,
    public readonly error?: {
      name: string;
      message: string;
    },
    public readonly endTime?: number,
  ) {}

  toSerializable(): Record<string, any> {
    const serialized: Record<string, any> = {
      type: this.type,
      method: this.method,
      url: this.url,
      timestamp: this.timestamp,
      status: this.status,
      duration: this.duration,
      error: this.error,
      startTime: this.startTime,
      endTime: this.endTime,
      requestHeaders: this.requestWrapper?.headers([...SAFE_HEADERS]),
      requestBodySize: this.requestWrapper?.bodySize,
      responseHeaders: this.responseWrapper?.headers([...SAFE_HEADERS]),
      responseBodySize: this.responseWrapper?.bodySize,
    };

    return Object.fromEntries(Object.entries(serialized).filter(([_, v]) => v !== undefined));
  }
}
